import { useMemo, useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { effectiveNoteTextMax, noteTargetKey, type NoteEntry, type NoteTarget } from '@shared/flow/notes';
import { NoteComposer } from './NoteComposer';
import { NotesChat } from './NotesChat';
import { NotesHeader } from './NotesHeader';
import { NotesStart } from './NotesStart';
import { PeopleFilter } from './PeopleFilter';
import { MAX_MENTIONS, pruneMentions } from './MentionPicker';
import { PostingToCombo, postingLabel, swapPostingPrefix } from './PostingToCombo';
import { useFlowNotesStore } from './flow-notes-store';
import { entryMatchesPeople, renderBody } from './noteFormat';
import { useOpenNotes } from './useOpenNotes';
import { useFlowNotesThread, useMarkRead, useMyNoteIdentity, usePostNote } from './useFlowNotes';

/**
 * Body of the rail's Notes tab: record search and header, the open record's
 * thread (filtered by the People view filter) and a floating composer with its
 * "Posting to" destination. With no record open it is the start screen. Marks
 * the thread read only after it loaded successfully.
 */
export function NotesPane() {
  const target = useFlowNotesStore(s => s.target);
  const peopleFilter = useFlowNotesStore(s => s.peopleFilter);
  const postingTo = useFlowNotesStore(s => s.postingTo);
  const pending = useFlowNotesStore(s => s.pending);
  const myName = useMyNoteIdentity();
  const thread = useFlowNotesThread(target);
  useMarkRead(target);
  const { retry } = usePostNote();
  const openNotes = useOpenNotes();
  // Why the last destination change could not move the draft; shown only while that destination is selected.
  const [moveNotice, setMoveNotice] = useState<{ key: string; text: string } | null>(null);

  const entries = thread.data?.entries;
  const visibleEntries = useMemo(
    () => (target && entries ? entries.filter(entry => entryMatchesPeople(entry, target, peopleFilter)) : []),
    [entries, target, peopleFilter],
  );

  if (!target) {
    return (
      <div data-testid="notes-pane" className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <NotesStart />
      </div>
    );
  }

  const roster = thread.data?.roster ?? [];
  const recordName = thread.data?.targetName ?? roster.find(r => r.id === target.id)?.name ?? null;
  const composerTarget = postingTo ?? target;

  const chipHandlers = (entry: NoteEntry) => ({
    onContactClick: (contactId: string) =>
      openNotes({ kind: 'contact', id: contactId, accountId: entry.accountId }, { filter: 'all', entryPoint: 'chip' }),
    onAccountClick: (accountId: string) =>
      openNotes({ kind: 'account', id: accountId, accountId }, { filter: 'all', entryPoint: 'chip' }),
  });

  // The draft is keyed by the posting target, so a destination change MOVES the
  // draft to the new key. Only the `@Name` this pane inserted itself is swapped
  // (tracked in `draftPrefix`); text the rep typed is never stripped, and a draft
  // already on the destination is merged into rather than overwritten. If the
  // result would go over the author-aware limit or the mention cap, nothing is
  // moved or cut: both drafts stay where they are, the destination still
  // switches, and a plain notice says why.
  const changePostingTo = (next: NoteTarget) => {
    const store = useFlowNotesStore.getState();
    const from = store.postingTo ?? target;
    const fromKey = noteTargetKey(from);
    const toKey = noteTargetKey(next);
    if (fromKey !== toKey) {
      const limit = effectiveNoteTextMax(myName ?? '');
      const fromPrefix = store.draftPrefix[fromKey] ?? null;
      const toLabel = next.kind === 'contact' ? postingLabel(next, roster) : null;
      const moved = swapPostingPrefix(store.drafts[fromKey] ?? '', fromPrefix, null).trim();
      const existing = store.drafts[toKey] ?? '';
      const result =
        existing !== '' ? (moved === '' ? existing : `${existing}\n${moved}`) : moved !== '' ? swapPostingPrefix(moved, null, toLabel) : '';
      const mergedMentions = [...(store.draftMentions[toKey] ?? []), ...(store.draftMentions[fromKey] ?? [])];
      const mentions = pruneMentions(result, mergedMentions);
      const tooLong = result.length > limit;
      if (tooLong || mentions.length > MAX_MENTIONS) {
        const accountName = thread.data?.accountName;
        const reason = tooLong ? 'Your note is too long' : 'Your note has too many people tagged';
        setMoveNotice({
          key: toKey,
          text: `${reason} to move to ${postingLabel(next, roster, accountName)}, so it's still on ${postingLabel(from, roster, accountName)}.`,
        });
      } else {
        setMoveNotice(null);
        store.clearDraft(fromKey);
        if (result !== '') store.setDraft(toKey, result);
        if (existing === '' && moved !== '') store.setDraftPrefix(toKey, toLabel);
        if (mentions.length > 0) store.setDraftMentions(toKey, mentions);
      }
    }
    store.setPostingTo(next);
  };

  return (
    <div data-testid="notes-pane" className="flex min-h-0 flex-1 flex-col">
      <NotesHeader
        target={target}
        recordName={recordName}
        filterSlot={
          target.kind === 'account' && thread.data ? (
            <PeopleFilter
              entries={thread.data.entries}
              roster={roster}
              value={peopleFilter}
              onChange={filter => useFlowNotesStore.getState().setPeopleFilter(filter)}
            />
          ) : null
        }
      />
      <NotesChat
        target={target}
        entries={visibleEntries}
        myName={myName}
        pending={pending}
        onRetry={retry}
        onRefresh={() => void thread.refetch()}
        renderBody={entry => renderBody(entry.body, entry.mentions, chipHandlers(entry))}
        placeholder={
          thread.isError ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-6">
              <Text size="sm" variant="secondary">
                Couldn't load notes.
              </Text>
              <Button size="sm" variant="secondary" onClick={() => void thread.refetch()}>
                Retry
              </Button>
            </div>
          ) : !thread.data ? (
            <Text size="sm" variant="tertiary" className="block py-6 text-center">
              Loading notes…
            </Text>
          ) : undefined
        }
        composer={
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Text size="xs" variant="tertiary" className="shrink-0">
                Posting to
              </Text>
              <PostingToCombo
                accountId={target.accountId}
                roster={roster}
                rosterTruncated={thread.data?.rosterTruncated ?? false}
                accountName={thread.data?.accountName}
                value={composerTarget}
                onChange={changePostingTo}
              />
            </div>
            {moveNotice && moveNotice.key === noteTargetKey(composerTarget) && (
              <Text size="xs" variant="tertiary" role="status">
                {moveNotice.text}
              </Text>
            )}
            <NoteComposer target={composerTarget} myName={myName} />
          </div>
        }
      />
    </div>
  );
}
